import {Modal} from '@therms/atalaya'
import {
  ActivityTypeTagTitles,
  EventTypeTitles,
  LabeledInputDisplay,
  LocationNames,
  SEARCH_FILTER_FILTER_LABELS,
  SearchFilterActivityTypeMultiSelect,
  SearchFilterEventTypeActiveInactiveMultiSelect,
  SearchFilterLocationMultiSelect,
  SearchFilterUserActiveInactiveMultiSelect,
  useLocationContext,
  UserNames,
} from '@therms/react-modules'
import classnames from 'classnames'
import {useState} from 'react'

interface EventsSearchFiltersModalProps {
  filtersState: {
    selectedActivityTypeIds?: string[] | undefined
    selectedDateTimeRange?: {start?: string; end?: string}
    selectedEventTypeIds?: string[] | undefined
    selectedUserIds?: string[] | undefined
    selectedLocationId?: string | undefined
  }
  onClose: () => void
  setFiltersState: (
    val: Partial<{
      selectedActivityTypeIds?: string[]
      selectedDateTimeRange?: {start?: string; end?: string}
      selectedEventTypeIds?: string[]
      selectedLocationId: string
      selectedUserIds: string[]
    }>,
  ) => void
}
function EventsSearchFiltersModal({
  filtersState,
  onClose,
  setFiltersState,
}: EventsSearchFiltersModalProps) {
  const [focusedFilter, setFocusedFilter] = useState<
    SEARCH_FILTER_FILTER_LABELS | ''
  >()

  const locationCtx = useLocationContext()

  return (
    <Modal
      closeHandler={onClose}
      escPressClose
      header="Advanced Search Filters"
      outsideClickClose
    >
      <div className="gap-y-xs flex flex-col">
        <div
          className={classnames(
            {
              'gap-y-xxs': !locationCtx,
              'grid-cols-2': !locationCtx,
              'grid-cols-3': !!locationCtx,
            },
            'gap-x-xxs grid hover:cursor-pointer',
          )}
        >
          <div
            onClick={() => {
              if (focusedFilter === SEARCH_FILTER_FILTER_LABELS.ACTIVITY_TYPE) {
                setFocusedFilter('')
              } else {
                setFocusedFilter(SEARCH_FILTER_FILTER_LABELS.ACTIVITY_TYPE)
              }
            }}
          >
            <LabeledInputDisplay
              displayOnly
              label="Activity Types"
              nameElement={
                <ActivityTypeTagTitles
                  activityTypeTagIds={
                    filtersState.selectedActivityTypeIds || []
                  }
                  listAll={false}
                />
              }
              value={filtersState.selectedActivityTypeIds}
            />
          </div>

          <div
            onClick={() => {
              if (focusedFilter === SEARCH_FILTER_FILTER_LABELS.USER) {
                setFocusedFilter('')
              } else {
                setFocusedFilter(SEARCH_FILTER_FILTER_LABELS.USER)
              }
            }}
          >
            <LabeledInputDisplay
              displayOnly
              label="Assigned Users"
              nameElement={
                <UserNames
                  userIds={filtersState.selectedUserIds || []}
                  listAll={false}
                />
              }
              value={filtersState.selectedUserIds}
            />
          </div>

          <div
            onClick={() => {
              if (focusedFilter === SEARCH_FILTER_FILTER_LABELS.EVENT_TYPE) {
                setFocusedFilter('')
              } else {
                setFocusedFilter(SEARCH_FILTER_FILTER_LABELS.EVENT_TYPE)
              }
            }}
          >
            <LabeledInputDisplay
              displayOnly
              label="Event Types"
              nameElement={
                <EventTypeTitles
                  eventTypeIds={filtersState.selectedEventTypeIds || []}
                  listAll={false}
                />
              }
              value={filtersState.selectedEventTypeIds}
            />
          </div>

          {!locationCtx && (
            <div
              onClick={() => {
                if (focusedFilter === SEARCH_FILTER_FILTER_LABELS.LOCATION) {
                  setFocusedFilter('')
                } else {
                  setFocusedFilter(SEARCH_FILTER_FILTER_LABELS.LOCATION)
                }
              }}
            >
              <LabeledInputDisplay
                displayOnly
                label="Location"
                nameElement={
                  <LocationNames
                    locationIds={
                      filtersState.selectedLocationId
                        ? [filtersState.selectedLocationId]
                        : []
                    }
                    listAll={false}
                  />
                }
                value={filtersState.selectedLocationId}
              />
            </div>
          )}
        </div>

        <SearchFilterActivityTypeMultiSelect
          focused={focusedFilter === SEARCH_FILTER_FILTER_LABELS.ACTIVITY_TYPE}
          onChange={(val) => {
            setFiltersState({selectedActivityTypeIds: val})
          }}
          onClearSelection={() => {
            setFiltersState({selectedActivityTypeIds: undefined})
          }}
          onClick={() => {
            if (focusedFilter === SEARCH_FILTER_FILTER_LABELS.ACTIVITY_TYPE) {
              setFocusedFilter('')
            } else {
              setFocusedFilter(SEARCH_FILTER_FILTER_LABELS.ACTIVITY_TYPE)
            }
          }}
          value={filtersState.selectedActivityTypeIds}
        />

        <SearchFilterUserActiveInactiveMultiSelect
          focused={focusedFilter === SEARCH_FILTER_FILTER_LABELS.USER}
          label="Assigned Users"
          onChange={(val) =>
            setFiltersState({
              selectedUserIds: val,
            })
          }
          onClearSelection={() => {
            setFiltersState({selectedUserIds: undefined})
          }}
          onClick={() => {
            if (focusedFilter === SEARCH_FILTER_FILTER_LABELS.USER) {
              setFocusedFilter('')
            } else {
              setFocusedFilter(SEARCH_FILTER_FILTER_LABELS.USER)
            }
          }}
          value={filtersState.selectedUserIds}
        />

        <SearchFilterEventTypeActiveInactiveMultiSelect
          focused={focusedFilter === SEARCH_FILTER_FILTER_LABELS.EVENT_TYPE}
          onChange={(val) => {
            setFiltersState({selectedEventTypeIds: val})
          }}
          onClearSelection={() => {
            setFiltersState({selectedEventTypeIds: undefined})
          }}
          onClick={() => {
            if (focusedFilter === SEARCH_FILTER_FILTER_LABELS.EVENT_TYPE) {
              setFocusedFilter('')
            } else {
              setFocusedFilter(SEARCH_FILTER_FILTER_LABELS.EVENT_TYPE)
            }
          }}
          value={filtersState.selectedEventTypeIds}
        />

        {!locationCtx && (
          <SearchFilterLocationMultiSelect
            focused={focusedFilter === SEARCH_FILTER_FILTER_LABELS.LOCATION}
            label="Location"
            onChange={(val) => {
              if (filtersState.selectedLocationId && !!val?.length) {
                const filtered = val.filter(
                  (_val) => filtersState.selectedLocationId !== _val,
                )
                setFiltersState({
                  selectedLocationId: filtered[0],
                })
              } else {
                setFiltersState({
                  selectedLocationId: val[0],
                })
              }
            }}
            onClearSelection={() => {
              setFiltersState({selectedLocationId: undefined})
            }}
            onClick={() => {
              if (focusedFilter === SEARCH_FILTER_FILTER_LABELS.LOCATION) {
                setFocusedFilter('')
              } else {
                setFocusedFilter(SEARCH_FILTER_FILTER_LABELS.LOCATION)
              }
            }}
            value={
              filtersState.selectedLocationId
                ? [filtersState.selectedLocationId]
                : undefined
            }
          />
        )}
      </div>
    </Modal>
  )
}
export {EventsSearchFiltersModal}
